// src/components/Admin/Commissions.js
import React, { useEffect, useMemo, useState } from "react";
import "../../styles/AdminCommissions.css";
import config from "../../config";

function getCurrentMonthRange() {
  const today = new Date();
  const y = today.getFullYear();
  const m = String(today.getMonth() + 1).padStart(2, "0");
  const d = String(today.getDate()).padStart(2, "0");

  return {
    from: `${y}-${m}-01`,
    to: `${y}-${m}-${d}`,

  };
}

function formatCurrency(value) {
  if (value == null || isNaN(value)) return "₹0.00";
  return `₹${Number(value).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export default function Commissions() {
  const monthRange = getCurrentMonthRange();

  const [fromDate, setFromDate] = useState(monthRange.from);
  const [toDate, setToDate] = useState(monthRange.to);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [monthTotalCommission, setMonthTotalCommission] = useState(0);

  const token = localStorage.getItem("admin_id_token");

  const headers = useMemo(() => {
    const h = { Accept: "application/json" };
    if (token) h.Authorization = `Bearer ${token}`;
    return h;
  }, [token]);

  // ------- LOAD COMMISSION FOR CURRENT FILTER -------
  const handleLoad = async () => {
    if (!fromDate || !toDate) {
      alert("Please select From and To dates.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const url = `${config.baseUrl}/api/admin/payouts/reports/commission?from=${fromDate}&to=${toDate}`;

      const res = await fetch(url, { method: "GET", headers });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to fetch commissions");
      }

      const data = await res.json();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Admin commissions error:", e);
      setError(e.message || "Failed to fetch commissions");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // ------- LOAD "THIS MONTH" SUMMARY (TOP RIGHT CARD) -------
  const loadThisMonthCommission = async () => {
    try {
      const url = `${config.baseUrl}/api/admin/payouts/reports/commission?from=${monthRange.from}&to=${monthRange.to}`;
      const res = await fetch(url, { method: "GET", headers });
      if (!res.ok) return;

      const data = await res.json();
      const total = (Array.isArray(data) ? data : []).reduce(
        (sum, row) => sum + (row.totalCommission || 0),
        0
      );
      setMonthTotalCommission(total);
    } catch (e) {
      // we can ignore errors here, page still works
      console.warn("Failed to load month commission", e);
    }
  };

  // Initial load: current month for table + "this month" card
  useEffect(() => {
    handleLoad();
    loadThisMonthCommission();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------- SUMMARY FROM CURRENT TABLE -------
  const summary = useMemo(() => {
    const base = {
      totalGross: 0,
      totalCommission: 0,
      totalSellerEarning: 0,
      paidCommission: 0,
      unpaidCommission: 0,
    };

    rows.forEach((r) => {
      base.totalGross += r.totalGross || 0;
      base.totalCommission += r.totalCommission || 0;
      base.totalSellerEarning += r.totalSellerEarning || 0;
      base.paidCommission += r.paidCommission || 0;
      base.unpaidCommission += r.unpaidCommission || 0;
    });

    return base;
  }, [rows]);

  return (
    <div className="comm-container">
      <div className="comm-header">
        <div>
          <h2>📈 Commission Details</h2>
          <p>Total commission Aurakart earns from all sellers.</p>
        </div>

        <div className="comm-filters">
          <div className="comm-date-field">
            <label>From</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>
          <div className="comm-date-field">
            <label>To</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
          <button
            className="comm-load-btn"
            onClick={handleLoad}
            disabled={loading}
          >
            {loading ? "Loading..." : "Load Commissions"}
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="comm-summary-row">
        <div className="comm-card">
          <div className="comm-card-title">Total Commission (Loaded Range)</div>
          <div className="comm-card-value">
            {formatCurrency(summary.totalCommission)}
          </div>
          <div className="comm-card-sub">
            Period:{" "}
            {fromDate && toDate ? `${fromDate} → ${toDate}` : "Not selected"}
          </div>
        </div>

        <div className="comm-card comm-card-blue">
          <div className="comm-card-title">This Month&apos;s Commission</div>
          <div className="comm-card-value">
            {formatCurrency(monthTotalCommission)}
          </div>
          <div className="comm-card-sub">Current calendar month</div>
        </div>

        <div className="comm-card comm-card-small">
          <div className="comm-card-title">Paid vs Pending</div>
          <div className="comm-card-sub">
            Paid: <strong>{formatCurrency(summary.paidCommission)}</strong>
          </div>
          <div className="comm-card-sub">
            Pending:{" "}
            <strong className="comm-pending">
              {formatCurrency(summary.unpaidCommission)}
            </strong>
          </div>
        </div>
      </div>

      {error && <div className="comm-error">Failed to fetch commissions – {error}</div>}

      <div className="comm-table-wrap">
        {rows.length === 0 ? (
          <div className="comm-empty">
            📉 No commission records for this period yet.
          </div>
        ) : (
          <table className="comm-table">
            <thead>
              <tr>
                <th>Seller</th>
                <th>Total Gross</th>
                <th>Total Commission</th>
                <th>Paid Commission</th>
                <th>Pending Commission</th>
                <th>Total Seller Earning</th>
                <th>Payouts</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.sellerId}>
                  <td>
                    <div className="comm-seller">
                      <span className="comm-seller-name">{row.sellerName}</span>
                      <span className="comm-seller-id">
                        ID: {row.sellerId}
                      </span>
                    </div>
                  </td>
                  <td>{formatCurrency(row.totalGross)}</td>
                  <td>{formatCurrency(row.totalCommission)}</td>
                  <td>{formatCurrency(row.paidCommission)}</td>
                  <td className="comm-pending">
                    {formatCurrency(row.unpaidCommission)}
                  </td>
                  <td>{formatCurrency(row.totalSellerEarning)}</td>
                  <td>{row.payoutCount || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
